import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, ConfirmDialog, DataTable, EmptyState, LoadingState, type Column } from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import {
  deleteDynamicClient,
  listDynamicClients,
  revokeDynamicClient,
  setClientVerified,
  type DynamicClient,
} from "../lib/api/oauth";

type Pending = { kind: "revoke" | "delete"; client: DynamicClient } | null;

export function AdminOAuthClientsRoute() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["oauth-clients"] });

  const clientsQuery = useQuery({ queryKey: ["oauth-clients"], queryFn: listDynamicClients });
  const verifyMutation = useMutation({
    mutationFn: (v: { id: string; verified: boolean }) => setClientVerified(v.id, v.verified),
    onSuccess: refresh,
  });
  const actionMutation = useMutation({
    mutationFn: async (p: NonNullable<Pending>) => {
      if (p.kind === "revoke") {
        const r = await revokeDynamicClient(p.client.client_id);
        return `${p.client.name} was disabled and ${r.grants_revoked} grant(s) were revoked.`;
      }
      await deleteDynamicClient(p.client.client_id);
      return `${p.client.name} was deleted and its grants revoked.`;
    },
    onSuccess: (msg) => {
      setPending(null);
      setNotice(msg);
      refresh();
    },
  });

  const columns: Column<DynamicClient>[] = [
    {
      key: "name",
      label: "Client",
      render: (c) => (
        <div>
          <div className="font-medium">{c.name}</div>
          <div className="font-mono text-xs text-[var(--color-neutral-500)]">{c.client_id}</div>
        </div>
      ),
    },
    {
      key: "redirects",
      label: "Redirect addresses",
      render: (c) => (
        <ul className="font-mono text-xs">
          {c.redirect_uris.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      ),
    },
    {
      key: "state",
      label: "Status",
      render: (c) => (
        <div className="flex flex-wrap gap-1">
          {c.verified ? <Badge variant="success">Verified</Badge> : <Badge variant="warning">Unverified</Badge>}
          {c.disabled && <Badge variant="danger">Disabled</Badge>}
        </div>
      ),
    },
    { key: "grants", label: "Active grants", render: (c) => String(c.active_grants) },
    { key: "ip", label: "Registered from", render: (c) => c.registered_ip || "unknown" },
    { key: "created", label: "Registered", render: (c) => new Date(c.created_at).toLocaleString() },
    { key: "used", label: "Last used", render: (c) => (c.last_used_at ? new Date(c.last_used_at).toLocaleString() : "never") },
    {
      key: "actions",
      label: "",
      render: (c) => (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={verifyMutation.isPending}
            onClick={() => verifyMutation.mutate({ id: c.client_id, verified: !c.verified })}
            aria-label={`${c.verified ? "Mark unverified" : "Mark verified"}: ${c.name}`}
          >
            {c.verified ? "Mark unverified" : "Mark verified"}
          </Button>
          {!c.disabled && (
            <Button size="sm" variant="secondary" onClick={() => setPending({ kind: "revoke", client: c })} aria-label={`Revoke ${c.name}`}>
              Revoke
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => setPending({ kind: "delete", client: c })} aria-label={`Delete ${c.name}`}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Dynamic OAuth clients</h1>
      <p className="text-sm text-[var(--color-neutral-500)]">
        Apps that registered themselves through dynamic client registration, usually Claude connectors. Anyone can register
        one, so each starts <strong>unverified</strong> and the consent screen warns the person approving it. Mark a client
        verified only after you have confirmed who runs it and that its redirect address is theirs. Revoking disables the
        client and revokes every grant it holds; deleting also removes the client.
      </p>
      {notice && (
        <Alert variant="success" title="Done">
          {notice}
        </Alert>
      )}
      {(verifyMutation.isError || actionMutation.isError) && (
        <Alert variant="danger" title="Action failed">
          {apiErrorMessage(verifyMutation.error ?? actionMutation.error)}
        </Alert>
      )}
      {clientsQuery.isLoading ? (
        <LoadingState message="Loading clients..." />
      ) : clientsQuery.isError ? (
        <Alert variant="danger" title="Failed to load clients">
          {apiErrorMessage(clientsQuery.error)}
        </Alert>
      ) : (clientsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No dynamic clients" description="Nothing has registered itself yet." />
      ) : (
        <Card padding="none">
          <DataTable<DynamicClient> data={clientsQuery.data!} columns={columns} emptyMessage="No clients." />
        </Card>
      )}
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending?.kind === "delete" ? "Delete client" : "Revoke client"}
        message={
          pending
            ? `${pending.client.name} has ${pending.client.active_grants} active grant(s). They will all be revoked${
                pending.kind === "delete" ? " and the client removed" : " and the client disabled"
              }.`
            : ""
        }
        confirmText={pending?.kind === "delete" ? "Delete" : "Revoke"}
        variant="danger"
        onConfirm={() => pending && actionMutation.mutate(pending)}
      />
    </div>
  );
}
